import { MusicalTimeline } from "@/lib/music/musical-timeline";

/**
 * La grille d'une séance : ticks ↔ positions, la même chez tout le monde.
 *
 * # Pourquoi c'est un fait de séance
 *
 * Une note voyage avec son tick, et chaque récepteur le convertit en instant
 * avec **sa** grille. Deux grilles différentes, et la note tombe ailleurs — de
 * plus en plus loin à mesure que la séance avance, puisque l'écart entre deux
 * tempos s'accumule. Découvert le 2026-09-14 : un interprète encore sur la
 * grille à 90 BPM laissée par le jingle d'accueil posait ses notes à 720 ticks
 * par seconde ; un spectateur à 120 BPM les aurait vues arriver de plus en plus
 * tard. La grille dépendait de l'histoire de chaque navigateur.
 *
 * Elle se lit donc dans l'état publié, et nulle part ailleurs : un tempo de
 * départ, et les changements de tempo, chacun ancré à une position **et** au
 * tick que cette position avait. Changer de tempo est une commande au service,
 * comme la pause.
 *
 * Le chiffrage aussi (`SessionSignature`) : jusqu'au 2026-09-22 il ne quittait
 * pas le métronome, toute séance restait en 4/4 et un récital joué en 6/8 se
 * relisait en 4/4.
 *
 * # Pourquoi chaque marque porte aussi son tick
 *
 * Le service ne connaît pas les ticks, et garde un nombre borné de marques.
 * Avec la position seule, perdre les plus anciennes rendrait faux tous les
 * ticks qui suivent. Avec le tick, la conversion reste exacte à partir de la
 * plus ancienne marque gardée : avant elle, un tempo uniforme relie l'origine à
 * cette marque, et le passé lointain d'une longue séance se replace à peu près,
 * sans rien déplacer du présent.
 */

/** Ticks par noire de la grille de séance : le défaut du studio. */
export const SESSION_PPQ = 480;

/** Tempo d'une séance qui n'en publie aucun. Le défaut du service. */
export const DEFAULT_SESSION_BPM = 120;

/** Les bornes du service pour un tempo de séance, en BPM. */
const MIN_SESSION_BPM = 10;
const MAX_SESSION_BPM = 400;

/**
 * Le chiffrage de la séance — 4/4, 6/8 : `beats` temps de valeur `unit` par
 * mesure, comme le service le publie.
 *
 * Un fait de séance au même titre que le tempo : deux clients qui n'ont pas le
 * même placent les barres de mesure à deux endroits, et un enregistrement le
 * relit pour écrire ses mesures. Il n'a pas d'historique — une séance n'en
 * porte qu'un à la fois.
 */
export interface SessionSignature {
  beats: number;
  unit: number;
}

/** Le chiffrage d'une séance qui n'en publie aucun. Le défaut du service. */
export const DEFAULT_SESSION_SIGNATURE: SessionSignature = { beats: 4, unit: 4 };

/** Les unités qu'accepte le service : une ronde et ses divisions. */
const SIGNATURE_UNITS = new Set([1, 2, 4, 8, 16, 32]);

/** Le plus de temps par mesure qu'accepte le service. */
const MAX_SIGNATURE_BEATS = 32;

/**
 * Lit le chiffrage publié, ou le défaut quand il manque ou ne tient pas dans
 * les bornes du service : une séance ouverte avant lui est en 4/4.
 */
export function parseSessionSignature(raw: unknown): SessionSignature {
  if (typeof raw !== "object" || raw === null) {
    return DEFAULT_SESSION_SIGNATURE;
  }

  const { beats, unit } = raw as Record<string, unknown>;

  if (
    typeof beats !== "number"
    || !Number.isInteger(beats)
    || beats < 1
    || beats > MAX_SIGNATURE_BEATS
    || typeof unit !== "number"
    || !SIGNATURE_UNITS.has(unit)
  ) {
    return DEFAULT_SESSION_SIGNATURE;
  }

  return { beats, unit };
}

/** Deux chiffrages qui placent les barres au même endroit. */
export function sameSignature(a: SessionSignature, b: SessionSignature): boolean {
  return a.beats === b.beats && a.unit === b.unit;
}

/** Le tempo tel qu'il part au service : entier, et dans ses bornes. */
export function proposedBpm(bpm: number): number {
  return Math.round(Math.min(MAX_SESSION_BPM, Math.max(MIN_SESSION_BPM, bpm)));
}

/** Un changement de tempo : à partir de cette position, et de ce tick, ce tempo. */
export interface TempoMark {
  positionMs: number;
  tick: number;
  bpm: number;
}

/**
 * Lit les marques publiées, tolérante : une marque illisible est ignorée, les
 * autres gardées, rangées par position.
 */
export function parseTempoMarks(raw: unknown): TempoMark[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const marks: TempoMark[] = [];

  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) {
      continue;
    }

    const record = entry as Record<string, unknown>;
    const positionMs = record.position_ms;
    const tick = record.tick;
    const bpm = record.bpm;

    if (!isFiniteNumber(positionMs) || !isFiniteNumber(tick) || !isFiniteNumber(bpm) || bpm <= 0) {
      continue;
    }

    marks.push({ positionMs, tick, bpm });
  }

  return marks.sort((a, b) => a.positionMs - b.positionMs);
}

/** Le tempo en vigueur au bout de la grille : la dernière marque, sinon le tempo de départ. */
export function sessionBpm(tempoBpm: number, marks: readonly TempoMark[]): number {
  if (marks.length > 0) {
    return marks[marks.length - 1].bpm;
  }

  return tempoBpm > 0 ? tempoBpm : DEFAULT_SESSION_BPM;
}

/**
 * Une clé qui change quand la grille change, et seulement alors : remplacer la
 * grille du transport fait tout redessiner, on ne le fait pas à chaque état.
 */
export function sessionTempoKey(
  tempoBpm: number,
  marks: readonly TempoMark[],
  signature: SessionSignature = DEFAULT_SESSION_SIGNATURE,
): string {
  return [
    tempoBpm,
    `${signature.beats}/${signature.unit}`,
    ...marks.map((mark) => `${mark.positionMs}:${mark.tick}:${mark.bpm}`),
  ].join("|");
}

/** Un changement de tempo, au tick où il prend effet. */
export interface TempoChange {
  tick: number;
  bpm: number;
}

/**
 * La carte des tempos d'une séance, vue depuis `fromTick` : le tempo en
 * vigueur à ce tick, et les changements qui suivent, leurs ticks comptés
 * depuis lui.
 *
 * C'est la carte de `buildSessionTimeline`, rebasée : les prises d'un
 * enregistrement repartent chacune de zéro, et une prise jouée après un
 * changement de tempo doit le trouver à son début.
 */
export function sessionTempoMap(
  tempoBpm: number,
  marks: readonly TempoMark[],
  ppq: number = SESSION_PPQ,
  fromTick = 0,
): { bpm: number; changes: TempoChange[] } {
  if (marks.length === 0) {
    return { bpm: tempoBpm > 0 ? tempoBpm : DEFAULT_SESSION_BPM, changes: [] };
  }

  const first = marks[0];

  // Avant la première marque, le tempo qui relie l'origine à elle : le tempo de
  // départ si elle n'a rien perdu, un tempo uniforme équivalent si des marques
  // plus anciennes ont été oubliées par le service.
  let bpm = first.tick > 0 && first.positionMs > 0
    ? (first.tick / ppq) * 60 / (first.positionMs / 1000)
    : first.bpm;
  const changes: TempoChange[] = [];

  for (const mark of marks) {
    if (mark.tick <= 0) {
      continue;
    }

    if (mark.tick <= fromTick) {
      bpm = mark.bpm;

      continue;
    }

    changes.push({ tick: mark.tick - fromTick, bpm: mark.bpm });
  }

  return { bpm, changes };
}

/**
 * Construit la grille d'une séance.
 *
 * Exacte à chaque marque : `tickToSeconds(marque.tick)` vaut sa position.
 */
export function buildSessionTimeline(
  tempoBpm: number,
  marks: readonly TempoMark[],
  ppq: number = SESSION_PPQ,
  signature: SessionSignature = DEFAULT_SESSION_SIGNATURE,
): MusicalTimeline {
  const map = sessionTempoMap(tempoBpm, marks, ppq);
  const timeline = new MusicalTimeline(ppq, map.bpm, {
    numerator: signature.beats,
    denominator: signature.unit,
  });

  for (const change of map.changes) {
    timeline.addTempo(change.tick, change.bpm);
  }

  return timeline;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
